import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Tooltip } from "antd";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import CustomIcon from "../../../../Components/CustomIcon";
import copy from "copy-to-clipboard";
import { useToast } from "../../../../Hooks/useToast";
import { getSohColor, formatDaysAge, getEmptyCellText } from "../SOHBreakdown/SOHBreakdownTableConfig";
import { timeAgo } from "../SOHDetails/SOHDetailsTableConfig";

// ─── ID cell (battery ID or device ID) ────────────────────────────────────────
const IDCell = ({ text, isActive, onNavigate }) => {
  const { openNotification } = useToast();

  const handleCopy = (e) => {
    e.stopPropagation();
    copy(text);
    openNotification({ text: "Copied to Clipboard" });
  };

  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: isActive ? "#2DB55D" : "#9E9E9E",
        }}
      />
      <Typography
        variant="fw400s14lh24c0061CA"
        onClick={onNavigate}
        sx={{
          cursor: "pointer",
          "&:hover": { color: "#4D44D8 !important" },
        }}
      >
        {text}
      </Typography>
      <Tooltip title="Click to copy" placement="top">
        <IconButton
          disableRipple
          disableTouchRipple
          disableFocusRipple
          onClick={handleCopy}
        >
          <CustomIcon name="copy-icon" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── Column order ─────────────────────────────────────────────────────────────
export const RUL_TABLE_COLUMN_ORDER = [
  "batteryID",
  "rulCategory",
  "rulMonths",
  "rulCycles",
  "rulKm",
  "mfgDate",
  "soh",
  "bmsCycles",
  "oem",
  "onboardedOn",
  "actions",
];

// ─── Column definitions ───────────────────────────────────────────────────────
export const RUL_TABLE_COLUMNS_CONFIG = {
  batteryID: {
    title: "Battery ID",
    headerLabel: "Battery ID",
    dataIndex: "batteryID",
    key: "batteryID",
    width: 200,
    valueRenderingType: "battery-id-link",
    fixed: "left",
  },
  rulCategory: {
    title: "RUL Range",
    headerLabel: "RUL Range",
    dataIndex: "rulCategory",
    key: "rulCategory",
    valueRenderingType: "text",
    width: 140,
  },
  rulMonths: {
    title: "RUL (Months & Years)",
    headerLabel: "RUL (Months & Years)",
    dataIndex: "rulMonths",
    key: "rulMonths",
    valueRenderingType: "number",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 140,
  },
  rulCycles: {
    title: "RUL (Cycles)",
    headerLabel: "RUL (Cycles)",
    dataIndex: "rulCycles",
    key: "rulCycles",
    valueRenderingType: "number",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 140,
  },
  rulKm: {
    title: "RUL (km)",
    headerLabel: "RUL (km)",
    dataIndex: "rulKm",
    key: "rulKm",
    valueRenderingType: "number",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 130,
  },
  mfgDate: {
    title: "Manufacturing Date",
    headerLabel: "Manufacturing Date",
    dataIndex: "mfgDate",
    key: "mfgDate",
    valueRenderingType: "text",
    width: 175,
  },
  soh: {
    title: "SOH (%)",
    headerLabel: "SOH (%)",
    dataIndex: "soh",
    key: "soh",
    valueRenderingType: "number",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 110,
  },
  bmsCycles: {
    title: "BMS Cycles",
    headerLabel: "BMS Cycles",
    dataIndex: "bmsCycles",
    key: "bmsCycles",
    valueRenderingType: "number",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 130,
  },
  oem: {
    title: "OEM",
    headerLabel: "OEM",
    dataIndex: "oem",
    key: "oem",
    valueRenderingType: "text",
    width: 140,
  },
  onboardedOn: {
    title: "Onboarded On",
    headerLabel: "Onboarded On",
    dataIndex: "onboardedOn",
    key: "onboardedOn",
    valueRenderingType: "text",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
    width: 155,
  },
  actions: {
    title: "",
    headerLabel: "",
    dataIndex: "actions",
    key: "actions",
    valueRenderingType: "action-menu",
    width: 48,
    fixed: "right",
  },
};

// ─── Custom renderers factory (accepts navigate callback) ─────────────────────
export const getRulCustomRenderers = (onBatteryIDClick) => ({
  "days-to-duration": (record, column) => {
    const days = record?.[column.dataIndex];
    return (
      <Typography sx={{ fontSize: 13, color: "#202124" }}>
        {days == null || isNaN(days) ? getEmptyCellText(column, formatDaysAge(days)) : formatDaysAge(days)}
      </Typography>
    );
  },

  "digital-twin-link": (record) => (
    <IDCell
      text={record.deviceID}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  "battery-id-link": (record) => (
    <IDCell
      text={record.batteryID}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  "soh-progress-bar": (record, config) => {
    const soh = record?.sohEst;
    if (soh == null) return <Typography sx={{ fontSize: 13, color: "#9E9E9E" }}>{getEmptyCellText(config, "N/A")}</Typography>;
    const decimalPlaces = config?.decimalPlaces ?? 1;
    const color = getSohColor(soh);
    const width = Math.min(100, Math.max(0, soh));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 56, height: 5, bgcolor: "#E0E0E0", borderRadius: "3px", flexShrink: 0 }}>
          <Box sx={{ width: `${width}%`, height: "100%", bgcolor: color, borderRadius: "3px" }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#202124" }}>{soh.toFixed(decimalPlaces)}%</Typography>
      </Box>
    );
  },
  "time-ago": (record) => (
    <Typography variant="fw400s14lh24c3D4B5A">{timeAgo(record.addedOn)}</Typography>
  ),
});

// ─── Map a battery object to a table row ──────────────────────────────────────
const toRow = (b) => ({
  id:          b.batteryID,
  batteryID:   b.batteryID,
  imeiID:      b.imeiID,
  chassisNumber: b.chassisNumber,
  status:      b.status ?? "inactive",
  rulCategory: b.rulCategory ?? "—",
  rulMonths:   b.rulMonths != null ? parseFloat(b.rulMonths.toFixed(1)) : null,
  rulCycles:   b.rulCycles ?? null,
  rulKm:       b.rulKm ?? null,
  mfgDate:     b.mfgDateMs
    ? new Date(b.mfgDateMs).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "—",
  soh:         b.soh != null ? Math.round(b.soh) : null,
  bmsCycles:   b.bmsCycles ?? null,
  oem:         b.oem ?? "—",
  onboardedOn: b.onboardedOn ?? "—",
});

// ─── Table config builder ─────────────────────────────────────────────────────
export const getRulTableConfig = () => ({
  title: "Battery List",
  tableColumnsConfig: RUL_TABLE_COLUMNS_CONFIG,
  tableColumnOrder: RUL_TABLE_COLUMN_ORDER,
  emptyMessage: "No batteries found",
});

// ─── Data fetch — client-side sort + paginate ─────────────────────────────────
export const getRulTableData = async (batteries, pageNumber, sortBy, pageSize = 10) => {
  let rows = (batteries || []).map(toRow);

  if (sortBy?.length) {
    const { column, direction } = sortBy[0];
    rows = [...rows].sort((a, b) => {
      const aVal = a[column] ?? -Infinity;
      const bVal = b[column] ?? -Infinity;
      if (aVal < bVal) return direction === "ASC" ? -1 : 1;
      if (aVal > bVal) return direction === "ASC" ? 1 : -1;
      return 0;
    });
  }

  const total = rows.length;
  const start = (pageNumber - 1) * pageSize;
  return { data: rows.slice(start, start + pageSize), total };
};

// ─── Table action handler (CSV download) ──────────────────────────────────────
export const getRulApplyTableAction = (batteries) =>
  async (_selectedRowKeys, action, _isSelectAll) => {
    if (action !== "download") return [];
    return (batteries || []).map(toRow).map((r) => ({
      "Battery ID":         r.batteryID,
      "RUL Range":          r.rulCategory,
      "RUL (Months & Years)": r.rulMonths,
      "RUL (Cycles)":       r.rulCycles,
      "RUL (km)":           r.rulKm,
      "Manufacturing Date": r.mfgDate,
      "SOH (%)":            r.soh,
      "BMS Cycles":         r.bmsCycles,
      "OEM":                r.oem,
      "Onboarded On":       r.onboardedOn,
    }));
  };

export const getRulCSVFileName = () =>
  `RUL_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`;
